import { useCallback, useMemo, useState } from 'react'

import SortOrder from '~/client/src/shared/enums/SortOrder'
import { useDebouncedValue } from '~/client/src/shared/hooks/useDebouncedValue'
import {
  IResourceRecord,
  IResourceUiConfig,
  RESOURCE_NAME_KEY,
} from '~/client/src/shared/resources/definitions/types'
import {
  IResolvedResourceFieldProjection,
  IResourceFieldProjector,
} from '~/client/src/shared/resources/projection/types'

import { fromCompoundIds, toCompoundIds } from './filterCompoundIds'
import { ILocalDefaults } from './parseDefaultOptions'

export interface IResourceFacetEntry {
  value: string
  label: string
}

export interface IResolvedPickerField {
  facetEntries: IResourceFacetEntry[]
  groupEntries: IResourceFacetEntry[]
  searchText: string
}

// The picker's facet convention: unassigned is the absence of entries ('' facet
// value), while the projector emits explicit unassigned entries.
const toResolvedPickerField = (
  projection: IResolvedResourceFieldProjection,
): IResolvedPickerField => ({
  facetEntries: projection.isEmpty
    ? []
    : projection.filterEntries.map(entry => ({
        value: entry.id,
        label: entry.label,
      })),
  groupEntries: projection.isEmpty
    ? []
    : projection.groupEntries.map(entry => ({
        value: entry.id,
        label: entry.label,
      })),
  searchText: projection.searchText,
})

interface IResourcePickerStateConfig {
  mode: 'single' | 'multi'
  resourceTypeId: string
  uiConfig: IResourceUiConfig
  records: IResourceRecord[]
  searchableKeys: string[]
  initialSelectedIds?: string[]
  initialSortKey?: string | null
  initialSortOrder?: SortOrder
  initialGroupByKey?: string | null
  initialFilterSelection?: Map<string, Set<string>>
  customFilterMatchers?: Record<
    string,
    (record: IResourceRecord, selectedValues: Set<string>) => boolean
  >
  fieldProjector: IResourceFieldProjector
  /** Single mode: re-picking the selected record clears it. */
  allowEmptySelection?: boolean
}

export interface IResourcePickerState {
  mode: 'single' | 'multi'
  resourceTypeId: string
  noneGroupingKey: string
  initialSortKey: string | null
  initialSortOrder: SortOrder
  initialGroupByKey: string | null
  fieldProjector: IResourceFieldProjector
  searchQuery: string
  /** The records are narrowed by a search: the debounced query, not the typed one. */
  isSearchApplied: boolean
  sortKey: string | null
  sortOrder: SortOrder
  groupByKey: string | null
  /** Grouped by the resource's tree key: the list is one tree, each record a row. */
  isTreeGrouping: boolean
  allowNoneGrouping: boolean
  filterSelection: Map<string, Set<string>>
  selectedIds: Set<string>
  isCreating: boolean
  filteredRecords: IResourceRecord[]
  sortedRecords: IResourceRecord[]
  hasSelection: boolean
  filterSelectionAsIds: string[]
  setSearchQuery: (value: string) => void
  setSortKey: (key: string | null) => void
  setSortOrder: (order: SortOrder) => void
  setGroupByKey: (key: string | null) => void
  setFilterSelection: (ids: string[]) => void
  setDefaults: (defaults: ILocalDefaults) => void
  toggleItem: (id: string) => void
  toggleGroup: (items: IResourceRecord[]) => void
  startCreating: () => void
  stopCreating: () => void
  addCreatedItem: (id: string) => void
  resetSelection: () => void
  getResolvedField: (
    recordId: string,
    key: string,
  ) => IResolvedPickerField | undefined
}

export function useResourcePickerState(
  config: IResourcePickerStateConfig,
): IResourcePickerState {
  const {
    mode,
    resourceTypeId,
    uiConfig,
    fieldProjector,
    records: allRecords,
    searchableKeys,
    allowEmptySelection,
  } = config

  const noneGroupingKey = uiConfig.noneGroupingKey

  const resolvedInitialSortKey =
    config.initialSortKey !== undefined
      ? config.initialSortKey
      : uiConfig.defaultSortKey
  const resolvedInitialSortOrder =
    config.initialSortOrder ?? uiConfig.defaultSortOrder
  // A resource with its own picker groupings is always grouped; a saved grouping it does
  // not offer opens as its picker default.
  const allowNoneGrouping = !uiConfig.pickerGroupings
  const isGroupingOffered = (key: string | null | undefined) =>
    allowNoneGrouping ||
    !!uiConfig.pickerGroupings?.some(grouping => grouping.key === key)
  const resolvedInitialGroupByKey =
    config.initialGroupByKey !== undefined &&
    isGroupingOffered(config.initialGroupByKey)
      ? config.initialGroupByKey
      : (uiConfig.pickerDefaultGroupingKey ?? uiConfig.defaultGroupingKey)

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 200)
  const [sortKey, setSortKey] = useState<string | null>(resolvedInitialSortKey)
  const [sortOrder, setSortOrder] = useState(resolvedInitialSortOrder)
  const [groupByKey, setGroupByKey] = useState<string | null>(
    resolvedInitialGroupByKey,
  )
  const [filterSelection, setFilterSelectionRaw] = useState<
    Map<string, Set<string>>
  >(config.initialFilterSelection ?? new Map())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(config.initialSelectedIds ?? []),
  )
  const [isCreating, setIsCreating] = useState(false)

  const isTreeGrouping = groupByKey === uiConfig.pickerTreeGroupingKey

  const resolvedIndex = useMemo(() => {
    const allKeys = new Set<string>()
    for (const [facetKey] of filterSelection) {
      if (!config.customFilterMatchers?.[facetKey]) {
        allKeys.add(facetKey)
      }
    }
    for (const key of searchableKeys) allKeys.add(key)
    if (sortKey && sortKey !== RESOURCE_NAME_KEY) allKeys.add(sortKey)
    // The tree reads each record's parent itself.
    if (groupByKey && groupByKey !== noneGroupingKey && !isTreeGrouping) {
      allKeys.add(groupByKey)
    }

    const keys = Array.from(allKeys)
    const index = new Map<string, Map<string, IResolvedPickerField>>()
    for (const record of allRecords) {
      const values = new Map<string, IResolvedPickerField>()
      for (const key of keys) {
        values.set(
          key,
          toResolvedPickerField(fieldProjector.resolve(record, key)),
        )
      }
      index.set(record.id, values)
    }
    return index
  }, [
    allRecords,
    fieldProjector,
    filterSelection,
    groupByKey,
    noneGroupingKey,
    isTreeGrouping,
    searchableKeys,
    sortKey,
    config.customFilterMatchers,
  ])

  const resolve = useCallback(
    (recordId: string, key: string): IResolvedPickerField | undefined =>
      resolvedIndex.get(recordId)?.get(key),
    [resolvedIndex],
  )

  const filteredRecords = useMemo(() => {
    let records = allRecords.filter(r => !r.isDeleted)

    for (const [facetKey, selectedValues] of filterSelection) {
      if (selectedValues.size === 0) continue

      const customMatcher = config.customFilterMatchers?.[facetKey]
      if (customMatcher) {
        records = records.filter(record =>
          customMatcher(record, selectedValues),
        )
        continue
      }

      records = records.filter(r => {
        const value = resolve(r.id, facetKey)
        const facetValues = value?.facetEntries.length
          ? value.facetEntries.map(entry => entry.value)
          : ['']

        return facetValues.some(facetValue => selectedValues.has(facetValue))
      })
    }

    const q = debouncedSearchQuery.toLowerCase().trim()
    if (q) {
      records = records.filter(r => {
        if (r.name.toLowerCase().includes(q)) return true
        return searchableKeys.some(key => {
          const value = resolve(r.id, key)
          return value != null && value.searchText.toLowerCase().includes(q)
        })
      })
    }

    return records
  }, [
    allRecords,
    filterSelection,
    debouncedSearchQuery,
    searchableKeys,
    resolve,
    config.customFilterMatchers,
  ])

  const sortedRecords = useMemo(() => {
    if (!sortKey) return filteredRecords
    return fieldProjector.sortRecords(filteredRecords, sortKey, sortOrder)
  }, [fieldProjector, filteredRecords, sortKey, sortOrder])

  const hasSelection = selectedIds.size > 0

  const filterSelectionAsIds = useMemo(
    () => toCompoundIds(filterSelection),
    [filterSelection],
  )

  const toggleItem = useCallback(
    (id: string) => {
      setSelectedIds(prev => {
        const next = new Set(prev)
        if (mode === 'single') {
          const wasSelected = next.has(id)
          next.clear()
          if (!(allowEmptySelection && wasSelected)) next.add(id)
        } else if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        return next
      })
    },
    [mode, allowEmptySelection],
  )

  const toggleGroup = useCallback((items: IResourceRecord[]) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      const allSelected = items.every(item => next.has(item.id))
      if (allSelected) {
        items.forEach(item => next.delete(item.id))
      } else {
        items.forEach(item => next.add(item.id))
      }
      return next
    })
  }, [])

  const setFilterSelection = useCallback((compoundIds: string[]) => {
    setFilterSelectionRaw(fromCompoundIds(compoundIds))
  }, [])

  const setDefaults = useCallback((defaults: ILocalDefaults) => {
    setSortKey(defaults.sortKey)
    setSortOrder(defaults.sortOrder)
    setGroupByKey(defaults.groupByKey)
    setFilterSelectionRaw(fromCompoundIds(defaults.filterSelection))
  }, [])

  const startCreating = useCallback(() => setIsCreating(true), [])
  const stopCreating = useCallback(() => setIsCreating(false), [])

  const addCreatedItem = useCallback(
    (id: string) => {
      setSelectedIds(prev => {
        const next = new Set(mode === 'single' ? [] : prev)
        next.add(id)
        return next
      })
    },
    [mode],
  )

  const resetSelection = useCallback(() => {
    setSelectedIds(new Set(config.initialSelectedIds ?? []))
  }, [config.initialSelectedIds])

  return {
    mode,
    resourceTypeId,
    noneGroupingKey,
    initialSortKey: resolvedInitialSortKey,
    initialSortOrder: resolvedInitialSortOrder,
    initialGroupByKey: resolvedInitialGroupByKey,
    fieldProjector,
    searchQuery,
    isSearchApplied: debouncedSearchQuery.trim() !== '',
    sortKey,
    sortOrder,
    groupByKey,
    isTreeGrouping,
    allowNoneGrouping,
    filterSelection,
    selectedIds,
    isCreating,
    filteredRecords,
    sortedRecords,
    hasSelection,
    filterSelectionAsIds,
    setSearchQuery,
    setSortKey,
    setSortOrder,
    setGroupByKey,
    setFilterSelection,
    setDefaults,
    toggleItem,
    toggleGroup,
    startCreating,
    stopCreating,
    addCreatedItem,
    resetSelection,
    getResolvedField: resolve,
  }
}
